import { supabaseBrowser } from '../supabaseBrowser';
import { winnersCache, CACHE_TTL } from '../cache';

const supabase = supabaseBrowser;

// Public winner lists come from get_public_winners(), a database function that
// returns display fields only (first name + last initial, town, avatar). The
// winners' profiles themselves are private: players can't read other players'
// email, phone, date of birth or wallet any more. Its winner_image_url is the
// winner's photo, and it's null unless the winner agreed to it being shown.
const LOGO = '/assets/images/Rendalls_Competition_Logo-1762876642233.png';

const toWinner = (row) => {
  const name = row?.display_name ?? null;
  const prize = row?.competition_title || 'Competition Prize';
  return {
    id: row?.id,
    competitionId: row?.competition_id,
    userId: null,
    userName: name,
    name,
    userAvatar: row?.winner_image_url || row?.avatar_url || null,
    avatar: row?.winner_image_url || row?.avatar_url || LOGO,
    photo: row?.winner_image_url || null,
    avatarAlt: name ? `${name} - Winner of ${prize}` : null,
    prize,
    prizeValue: Number(row?.prize_value) || 0,
    winningNumber: row?.winning_number,
    prizePosition: row?.prize_position ?? null,
    wonAt: row?.win_date,
    winDate: row?.win_date,
    ticketNumber: `WN-${row?.winning_number?.toString()?.padStart(4, '0')}`,
    location: row?.winner_location || row?.town || 'United Kingdom',
    quote: row?.quote || null,
    verified: row?.claimed || false
  };
};

const fetchPublicWinners = async (limit, competitionId = null) => {
  const { data, error } = await supabase?.rpc('get_public_winners', {
    p_limit: limit,
    p_competition_id: competitionId
  });
  if (error) throw error;
  return (data || []).map(toWinner);
};

export const pastWinnersService = {
  async getAll(limit = 50, bypassCache = false) {
    // Check cache first
    const cacheKey = `all_winners_${limit}`;
    if (!bypassCache) {
      const cached = winnersCache.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const result = await fetchPublicWinners(limit);

      // Cache the result
      winnersCache.set(cacheKey, result, CACHE_TTL.WINNERS_LIST);

      return result;
    } catch (error) {
      console.error('Past winners service error:', error);
      return [];
    }
  },

  async getRecent(limit = 10, bypassCache = false) {
    // Check cache first
    const cacheKey = `recent_winners_${limit}`;
    if (!bypassCache) {
      const cached = winnersCache.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const result = await fetchPublicWinners(limit);

      // Cache the result
      winnersCache.set(cacheKey, result, CACHE_TTL.WINNERS_LIST);

      return result;
    } catch (error) {
      console.error('Past winners service error:', error);
      return [];
    }
  },

  async getByCompetition(competitionId) {
    if (!competitionId) return [];
    try {
      return await fetchPublicWinners(50, competitionId);
    } catch (error) {
      console.error('Past winners service error:', error);
      return [];
    }
  },

  // The signed-in player's own wins: the only past_winners rows a player can
  // read directly (policy users_can_view_own_winners).
  async getByUser(userId) {
    try {
      const { data, error } = await supabase
        ?.from('past_winners')
        ?.select(`
          id,
          competition_id,
          user_id,
          winning_number,
          win_date,
          claimed,
          competition:competitions(title, prize_value, draw_date)
        `)
        ?.eq('user_id', userId)
        ?.order('win_date', { ascending: false });
      
      if (error) {
        console.error('Error fetching winners by user:', error);
        throw error;
      }
      
      return data?.map(row => ({
        id: row?.id,
        competitionId: row?.competition_id,
        competitionTitle: row?.competition?.title,
        prizeValue: row?.competition?.prize_value,
        drawDate: row?.competition?.draw_date,
        winningNumber: row?.winning_number,
        wonAt: row?.win_date,
        ticketNumber: `WN-${row?.winning_number?.toString()?.padStart(4, '0')}`,
        verified: row?.claimed || false
      })) || [];
    } catch (error) {
      console.error('Past winners service error:', error);
      return [];
    }
  }
};